import { Shadow, TableItem, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { ResponsesStatementFixedIncomeResponse } from '@genial/services/dist/models';
import { Utils, useBreakpointValue } from '@genial/utils';
import React from 'react';

import { fixedIncomeStatememntsWidthCols } from '../../../../../../constants';
import { messageError } from '../../constants';

interface DetailsItemProps {
  data: ResponsesStatementFixedIncomeResponse;
}

export const DetailsItem: React.FC<DetailsItemProps> = ({ data }) => {
  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  const movementData = data?.statementFixedIncome?.movimentoDate
    ? Utils.Mask.DateFormat(data.statementFixedIncome.movimentoDate, 'dd/MM/yyyy')
    : messageError;

  const unitPriceValue =
    data?.statementFixedIncome?.unitPriceValue || data?.statementFixedIncome?.unitPriceValue === 0
      ? Utils.Mask.Money(data.statementFixedIncome.unitPriceValue)
      : messageError;

  const grossValue =
    data?.statementFixedIncome?.grossValue || data?.statementFixedIncome?.grossValue === 0
      ? Utils.Mask.Money(data.statementFixedIncome.grossValue)
      : messageError;

  const iofValue =
    data?.statementFixedIncome?.iofValue || data?.statementFixedIncome?.iofValue === 0
      ? Utils.Mask.Money(data.statementFixedIncome.iofValue)
      : messageError;

  const netValue =
    data?.statementFixedIncome?.netValue || data?.statementFixedIncome?.netValue === 0
      ? Utils.Mask.Money(data.statementFixedIncome.netValue)
      : messageError;

  const irValue =
    data?.statementFixedIncome?.irValue || data?.statementFixedIncome?.irValue === 0
      ? Utils.Mask.Money(data.statementFixedIncome.irValue)
      : messageError;

  if (isMobile) {
    return (
      <Shadow level={3} borderRadius={BoxRadius.sm} marginBottom={Spacing['inset.xs']}>
        <VStack
          p={Spacing['inline.xxxs']}
          space={Spacing['inline.nano']}
          borderRadius={BoxRadius.md}>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>Data de movimento</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {movementData}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>Tipo</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {data?.statementFixedIncome?.operationType || messageError}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>Taxa</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {data?.statementFixedIncome?.tax || messageError}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>Quantidade</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {data?.statementFixedIncome?.quantity || messageError}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>Preço Unitário</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {unitPriceValue}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>Valor bruto</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {grossValue}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>IOF</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {iofValue}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>IR</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {irValue}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize={Typography['fontsize.md']}>Valor líquido</Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
              {netValue}
            </Text>
          </HStack>
        </VStack>
      </Shadow>
    );
  }

  return (
    <Shadow marginBottom={Spacing['inset.nano']} level={3} borderRadius={BoxRadius.sm}>
      <HStack
        alignItems="center"
        justifyContent="space-between"
        py={Spacing['inline.xxxs']}
        px={{ base: 0, lg: Spacing['inline.quarck'] }}>
        <TableItem
          testId="statement-fixed-income-item-movementDate"
          width={fixedIncomeStatememntsWidthCols.movementDate.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {movementData}
          </Text>
        </TableItem>
        <TableItem
          testId="statement-fixed-income-item-type"
          width={fixedIncomeStatememntsWidthCols.type.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {data?.statementFixedIncome?.operationType || messageError}
          </Text>
        </TableItem>
        <TableItem
          testId="statement-fixed-income-item-rate"
          width={fixedIncomeStatememntsWidthCols.rate.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {data?.statementFixedIncome?.tax || messageError}
          </Text>
        </TableItem>
        <TableItem
          testId="statement-fixed-income-item-amout"
          width={fixedIncomeStatememntsWidthCols.amount.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {data?.statementFixedIncome?.quantity || messageError}
          </Text>
        </TableItem>
        <TableItem
          testId="statement-fixed-income-item-unit-price"
          width={fixedIncomeStatememntsWidthCols.unitPrice.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {unitPriceValue}
          </Text>
        </TableItem>
        <TableItem
          testId="statement-fixed-income-item-grossValue"
          width={fixedIncomeStatememntsWidthCols.grossValue.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {grossValue}
          </Text>
        </TableItem>
        <TableItem
          testId="statement-fixed-income-item-iof"
          width={fixedIncomeStatememntsWidthCols.iof.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {iofValue}
          </Text>
        </TableItem>
        <TableItem
          testId="statement-fixed-income-item-ir"
          width={fixedIncomeStatememntsWidthCols.ir.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {irValue}
          </Text>
        </TableItem>
        <TableItem
          testId="statement-fixed-income-item-netValue"
          width={fixedIncomeStatememntsWidthCols.netValue.desktop}>
          <Text fontSize={Typography['fontsize.md']} noOfLines={1} variant={'regular'}>
            {netValue}
          </Text>
        </TableItem>
      </HStack>
    </Shadow>
  );
};
